import { useEffect, useState } from "react";
import { searchTags } from "@/shared/api/newsquawk-headlines";
import { menuCategories } from "@/widget-components/newsquawk-headlines/models/categories";
import { exactTagFor, type TagHit } from "../../builderModel";

export interface KeywordTag {
  id: number;
  name: string;
  category?: string;
}

/** Answers kept for the session: a keyword names the same tag every time. */
const answers = new Map<string, KeywordTag | null>();

/** Only the latest keywords are looked up, so a pasted list stays cheap. */
const MAX_LOOKUPS = 12;

const menuTags = menuCategories.flatMap((group) => group.items);

/**
 * The tags the Keywords box's words name exactly — "opec" names OPEC — for
 * the one-click "+ OPEC" under the Tags box.
 *
 * A keyword matches text and a tag matches the desk's tagging, so adding the
 * tag widens the list on purpose; it's offered, never done. The curated menu
 * tags answer without a request.
 */
export function useKeywordTags(keywords: string[]): KeywordTag[] {
  const [, setVersion] = useState(0);
  const wanted = keywords.slice(-MAX_LOOKUPS).map((k) => k.trim().toLowerCase()).filter(Boolean);
  const key = wanted.join("\n");

  useEffect(() => {
    const controller = new AbortController();
    const pending = wanted.filter((word) => !answers.has(word));
    if (pending.length === 0) return;
    let cancelled = false;
    (async () => {
      for (const word of pending) {
        const menu = menuTags.find((t) => t.title.toLowerCase() === word);
        if (menu) {
          answers.set(word, { id: menu.id, name: menu.title, category: "category" });
          continue;
        }
        try {
          const hits = ((await searchTags(word, controller.signal)) ?? []) as TagHit[];
          answers.set(word, exactTagFor(word, hits) ?? null);
        } catch {
          // Quietly nothing: a suggestion missed costs nothing.
          if (controller.signal.aborted) return;
          answers.set(word, null);
        }
      }
      if (!cancelled) setVersion((v) => v + 1);
    })();
    return () => {
      cancelled = true;
      controller.abort();
    };
    // `key` stands for `wanted`, which is rebuilt each render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  const seen = new Set<number>();
  const found: KeywordTag[] = [];
  for (const word of wanted) {
    const tag = answers.get(word);
    if (!tag || seen.has(tag.id)) continue;
    seen.add(tag.id);
    found.push(tag);
  }
  return found;
}
